import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { csOAuth2LoginApi } from '@/api/csLogin';
import { isCsAuthenticated, setCsJwtAuth } from '@/utils/store';
import logoIcon from '@/assets/images/logo.svg';
import styles from './index.module.css';

/** 账号格式校验 */
function isValidAccount(account: string): boolean {
  return account.length >= 1;
}

export default function CsLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation('csLogin');
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [pwdVisible, setPwdVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 已登录用户重定向到客服首页
  useEffect(() => {
    if (isCsAuthenticated()) {
      navigate('/csHome', { replace: true });
    }
  }, [navigate]);

  const isFormValid = account.length > 0 && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 前端校验
    if (!isValidAccount(account)) {
      setError(t('csLogin:errors.accountRequired'));
      return;
    }
    if (password.length < 1) {
      setError(t('csLogin:errors.passwordRequired'));
      return;
    }

    setLoading(true);

    try {
      // 调用OAuth2 token端点，返回 OAuth2TokenRes
      const res = await csOAuth2LoginApi({ account, password });
      // 拦截器对OAuth2格式直接透传，res 即为 OAuth2TokenRes
      const { access_token, refresh_token } = res;
      // 存储JWT Token并自动从JWT解码提取用户信息
      setCsJwtAuth(access_token, refresh_token);
      navigate('/csHome', { replace: true });
    } catch (err) {
      // OAuth2错误格式（如 invalid_grant、invalid_client）已由httpClient解析为中文提示
      // 业务失败统一走 catch，err.message 即为后端 msg 或 OAuth2中文映射
      setError(err instanceof Error ? err.message : t('csLogin:errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* 头部区域：Logo */}
      <div className={styles.header}>
        <div className={styles.logoWrap}>
          <img src={logoIcon} alt={t('csLogin:alt.logo')} className={styles.logoImg} />
        </div>
      </div>

      {/* 主体内容区：表单 */}
      <form className={styles.main} onSubmit={handleSubmit}>
        <input
          className={styles.input}
          type="text"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          placeholder={t('csLogin:form.accountPlaceholder')}
          autoComplete="username"
        />

        <div className={styles.passwordWrap}>
          <input
            className={styles.input}
            type={pwdVisible ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('csLogin:form.passwordPlaceholder')}
            autoComplete="current-password"
          />
          <button
            type="button"
            className={styles.eyeBtn}
            onClick={() => setPwdVisible(!pwdVisible)}
            aria-label={pwdVisible ? t('csLogin:hidePassword') : t('csLogin:showPassword')}
            tabIndex={-1}
          >
            {pwdVisible ? <EyeOff size={24} /> : <Eye size={24} />}
          </button>
        </div>

        {error && <div className={styles.errorMsg}>{error}</div>}

        <button
          type="submit"
          className={`${styles.submitBtn} ${!isFormValid || loading ? styles.disabled : ''}`}
          disabled={!isFormValid || loading}
        >
          {loading ? <Loader2 size={20} className={styles.spin} /> : t('csLogin:form.submit')}
        </button>
      </form>
    </div>
  );
}
